// ---------------------------------------------------------------------------
// IMPORTS
// ---------------------------------------------------------------------------

// ------NODE MODULES---------------------------------------------------------
import { useDispatch } from 'react-redux';

import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';

// ------FILE MODULES---------------------------------------------------------
import { ROW_COUNT } from '../../../config/constants.config';
import { clearPresentationsCache } from '../../../helpers/reducers/adminPresentations';

import PresentationRequestTemplateRow from '../Rows/PresentationRequestTemplateRow.component';

import Hider from '../../shared/Hider/Hider.component';
import TableCell from '../../shared/Tables/TableCell.component';
import TableHeader from '../../shared/Tables/TableHeader.component';
import SpinnerLoader from '../../shared/Loader/SpinnerLoader.component';
import SingleTableRow from '../../shared/Tables/SingleTableRow.component';

import './PresentationRequestTemplateTable.component.css';

// ---------------------------------------------------------------------------
// PRIVATE
// ---------------------------------------------------------------------------
const DEFAULT_TRANSLATE = (string) => {
  return string;
};
const DEFAULT_SET_ERROR_POPUP = () => {};
const DEFAULT_IS_LOADING = false;
const DEFAULT_HIDE_PAGINATION = false;
const DEFAULT_HIDE_SEND_BUTTON = false;
const DEFAULT_HIDE_REQUEST_DETAILS = false;
const DEFAULT_PRESENTATION_COUNT = 0;
const DEFAULT_ROWS_PER_PAGE = ROW_COUNT[0];
const DEFAULT_PAGE = 0;
const DEFAULT_SET_PAGE = () => {};
const DEFAULT_PADDING_BOTTOM = 4;
const DEFAULT_PRESENTATION_REQUEST_TEMPLATES = [];

const requestDetailsText = 'presentation-request-template-table-details';

function getHeaders(translate, hideRequestDetails) {
  return [
    {
      text: 'presentation-request-template-table-request-name'
    },
    {
      text: 'presentation-request-template-table-purpose'
    },
    {
      text: 'presentation-request-template-table-valid-dates'
    },
    {
      text: 'presentation-request-template-table-file'
    },
    {
      text: 'presentation-request-template-table-delegated'
    },
    {
      text: requestDetailsText
    },
    {
      text: 'presentation-request-template-table-actions'
    }
  ].filter((header) => {
    if (hideRequestDetails && header.text === requestDetailsText) {
      return false;
    }

    return true;
  }).map((header) => {
    header.text = translate(header.text);
    return header;
  });
}

// ---------------------------------------------------------------------------
// EXPORTS
// ---------------------------------------------------------------------------
export default function PresentationRequestTemplateTable(props) {
  const translate = props.translate || DEFAULT_TRANSLATE;
  const setErrorPopup = props.setErrorPopup || DEFAULT_SET_ERROR_POPUP;
  const isLoading = props.isLoading || DEFAULT_IS_LOADING;
  const hidePagination = props.hidePagination || DEFAULT_HIDE_PAGINATION;
  const hideSendButton = props.hideSendButton || DEFAULT_HIDE_SEND_BUTTON;
  const hideRequestDetails = props.hideRequestDetails || DEFAULT_HIDE_REQUEST_DETAILS;
  const presentationCount = props.presentationCount || DEFAULT_PRESENTATION_COUNT;
  const rowsPerPage = props.rowsPerPage || DEFAULT_ROWS_PER_PAGE;
  const page = props.page || DEFAULT_PAGE;
  const setPage = props.setPage || DEFAULT_SET_PAGE;
  const paddingBottom = props.paddingBottom === undefined ? DEFAULT_PADDING_BOTTOM : props.paddingBottom;
  const presentationRequestTemplates = props.presentationRequestTemplates || DEFAULT_PRESENTATION_REQUEST_TEMPLATES;

  const dispatch = useDispatch();

  const headers = getHeaders(translate, hideRequestDetails);

  return (
    <Grid item paddingBottom={paddingBottom}>
      <TableContainer className='presentation-request-template-table-container'>
        <Table>
          <TableHeader headers={headers} />
          <TableBody>
            <SpinnerLoader
              isLoading={isLoading}
              text={translate('presentation-request-template-table-loading')}
              component={SingleTableRow}
              componentProps={{ colSpan: headers.length, cellProps: { useTypography: false } }}>
              {presentationRequestTemplates.map((presentationRequestTemplate, index) => (
                <PresentationRequestTemplateRow
                  key={index}
                  translate={translate}
                  setErrorPopup={setErrorPopup}
                  presentationRequestTemplate={presentationRequestTemplate}
                  hideRequestDetails={hideRequestDetails}
                  hideSendButton={hideSendButton}
                />
              ))}
            </SpinnerLoader>
            <Hider isHidden={hidePagination}>
              <TableRow>
                <TableCell colSpan={headers.length} useTypography={false}>
                  <TablePagination
                    component='div'
                    count={presentationCount}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[]}
                    page={page}
                    onPageChange={(e, newPage) => {
                      setPage(newPage);
                      dispatch(clearPresentationsCache());
                    }}
                  />
                </TableCell>
              </TableRow>
            </Hider>
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
}
